import { resolveLayoutName, type LayoutName } from "@workspace/model"

import { FEATURE_FLAGS, type ItemCardVariant } from "@/lib/feature-flags"

/**
 * How a layout presents the menu.
 *
 * - `categoryPages` — `false`: `/menu` is one long page with every category as
 *   a section, and category links jump to `/menu#category-<id>`. `true`: `/menu`
 *   is a grid of categories only, and each category has its own page
 *   (`/menu/category/<id>`), the way template takeaway sites run their menu.
 * - `itemCard` — the item render every browse list uses.
 */
export interface MenuPresentation {
  categoryPages: boolean
  itemCard: ItemCardVariant
}

const SECTIONS: MenuPresentation = {
  categoryPages: false,
  itemCard: FEATURE_FLAGS.itemCardVariant,
}

/**
 * Exhaustive per-layout table, like the layout and slot registries, so a new
 * layout name fails to compile until it picks a presentation — and pages ask
 * here instead of branching on the layout name.
 */
const MENU_PRESENTATIONS: Record<LayoutName, MenuPresentation> = {
  default: SECTIONS,
  classic: SECTIONS,
  express: SECTIONS,
  showcase: { categoryPages: true, itemCard: "row" },
}

export function menuPresentation(layout: unknown): MenuPresentation {
  return MENU_PRESENTATIONS[resolveLayoutName(layout)]
}

/**
 * The menu narrowed to items with a current offer — the header nav's "Offers"
 * link. Every layout serves it: a `categoryPages` layout lists the matches
 * flat, the way it lists search results.
 */
export const OFFERS_MENU_HREF = "/menu?offers=1"

/** A category's own page — only meaningful when `categoryPages` is on. */
export function categoryPageHref(categoryId: string): string {
  return `/menu/category/${encodeURIComponent(categoryId)}`
}

/** Where a category link goes under a presentation. */
export function categoryHref(
  presentation: Pick<MenuPresentation, "categoryPages">,
  categoryId: string
): string {
  return presentation.categoryPages
    ? categoryPageHref(categoryId)
    : `/menu#category-${categoryId}`
}
